import { ref, watch } from 'vue'

/**
 * UI Preferences composable for localStorage-based UI settings
 *
 * These are client-side only preferences that don't sync across devices.
 * Use this for UI customization options that are device-specific.
 */

// Storage keys
const STORAGE_KEYS = {
  COMPACT_ACTION_BUTTON: 'spectra_compact_action_button',
  PILL_TABS: 'spectra_pill_tabs',
  SHOW_CIRCLE_TAB: 'spectra_show_circle_tab',
  SLIDER_MODE: 'spectra_slider_mode',
  SLIDER_DIAL_ENABLED: 'spectra_slider_dial_enabled',
  BLUR_PLACEHOLDER_ENABLED: 'spectra_blur_placeholder_enabled'
}

// Valid slider modes
const SLIDER_MODES = ['horizontal', 'vertical']

// Reactive refs (singleton pattern - shared across all uses)
const compactActionButton = ref(true) // Permanently enabled
const pillTabs = ref(true) // Permanently enabled
const showCircleTab = ref(false) // Default to disabled
const sliderMode = ref('horizontal') // 'horizontal' | 'vertical'
const sliderDialEnabled = ref(false) // Default to disabled
const blurPlaceholderEnabled = ref(false) // Default to disabled - reduces PostREST egress

// Initialize from localStorage
function initFromStorage() {
  if (typeof window === 'undefined') return

  // Show circle tab preference
  const storedShowCircleTab = localStorage.getItem(STORAGE_KEYS.SHOW_CIRCLE_TAB)
  if (storedShowCircleTab !== null) {
    showCircleTab.value = storedShowCircleTab === 'true'
  }

  // Slider mode preference
  const storedSliderMode = localStorage.getItem(STORAGE_KEYS.SLIDER_MODE)
  if (storedSliderMode !== null && SLIDER_MODES.includes(storedSliderMode)) {
    sliderMode.value = storedSliderMode
  }

  // Slider dial preference
  const storedSliderDial = localStorage.getItem(STORAGE_KEYS.SLIDER_DIAL_ENABLED)
  if (storedSliderDial !== null) {
    sliderDialEnabled.value = storedSliderDial === 'true'
  }

  // Blur placeholder preference
  const storedBlurPlaceholder = localStorage.getItem(STORAGE_KEYS.BLUR_PLACEHOLDER_ENABLED)
  if (storedBlurPlaceholder !== null) {
    blurPlaceholderEnabled.value = storedBlurPlaceholder === 'true'
  }
}

// Initialize on module load
initFromStorage()

export function useUIPreferences() {
  /**
   * Set the compact action button preference
   * When true, shows a single FAB that opens a sheet instead of the action strip
   */
  const setCompactActionButton = (value) => {
    compactActionButton.value = value
    localStorage.setItem(STORAGE_KEYS.COMPACT_ACTION_BUTTON, String(value))
  }

  /**
   * Toggle the compact action button preference
   */
  const toggleCompactActionButton = () => {
    setCompactActionButton(!compactActionButton.value)
  }

  /**
   * Set the pill tabs preference
   * When true, shows rounded pill-style buttons instead of underlined tabs
   */
  const setPillTabs = (value) => {
    pillTabs.value = value
    localStorage.setItem(STORAGE_KEYS.PILL_TABS, String(value))
  }

  /**
   * Toggle the pill tabs preference
   */
  const togglePillTabs = () => {
    setPillTabs(!pillTabs.value)
  }

  /**
   * Set the show circle tab preference
   * When true, shows the Circle tab on title pages for activity from followed users
   */
  const setShowCircleTab = (value) => {
    showCircleTab.value = value
    localStorage.setItem(STORAGE_KEYS.SHOW_CIRCLE_TAB, String(value))
  }

  /**
   * Toggle the show circle tab preference
   */
  const toggleShowCircleTab = () => {
    setShowCircleTab(!showCircleTab.value)
  }

  /**
   * Set the slider input mode for review scores
   * 'horizontal' = standard horizontal bars, 'vertical' = mixing desk faders
   */
  const setSliderMode = (mode) => {
    if (!SLIDER_MODES.includes(mode)) return
    sliderMode.value = mode
    localStorage.setItem(STORAGE_KEYS.SLIDER_MODE, mode)
  }

  /**
   * Set the slider dial preference
   * When true, tapping the score/info expands a dial for precision control
   */
  const setSliderDialEnabled = (value) => {
    sliderDialEnabled.value = value
    localStorage.setItem(STORAGE_KEYS.SLIDER_DIAL_ENABLED, String(value))
  }

  const toggleSliderDialEnabled = () => {
    setSliderDialEnabled(!sliderDialEnabled.value)
  }

  /**
   * Set the blur placeholder preference
   * When true, review queries include blur_placeholder data for loading placeholders.
   * Disabled by default to reduce PostREST egress.
   */
  const setBlurPlaceholderEnabled = (value) => {
    blurPlaceholderEnabled.value = value
    localStorage.setItem(STORAGE_KEYS.BLUR_PLACEHOLDER_ENABLED, String(value))
  }

  const toggleBlurPlaceholderEnabled = () => {
    setBlurPlaceholderEnabled(!blurPlaceholderEnabled.value)
  }

  /**
   * Reset the developer-preference UI settings stored in localStorage to
   * their defaults. Used when revoking dev-pref access from non-admin users.
   * COMPACT_ACTION_BUTTON and PILL_TABS are permanently enabled and not reset.
   */
  const resetDevPreferencesToDefaults = () => {
    setShowCircleTab(false)
    setSliderMode('horizontal')
    setSliderDialEnabled(false)
    setBlurPlaceholderEnabled(false)
  }

  return {
    // State
    compactActionButton,
    pillTabs,
    showCircleTab,
    sliderMode,
    sliderDialEnabled,
    blurPlaceholderEnabled,

    // Methods
    setCompactActionButton,
    toggleCompactActionButton,
    setPillTabs,
    togglePillTabs,
    setShowCircleTab,
    toggleShowCircleTab,
    setSliderMode,
    setSliderDialEnabled,
    toggleSliderDialEnabled,
    setBlurPlaceholderEnabled,
    toggleBlurPlaceholderEnabled,
    resetDevPreferencesToDefaults
  }
}
